// #region Imports
import { setAssetPath as setIonicCoreAssetPath } from '@ionic/core/components';
import { addIcons } from 'ionicons';
import { setAssetPath as setIoniconsAssetPath } from 'ionicons/components';
import {
  add,
  addCircle,
  addCircleOutline,
  airplane,
  airplaneOutline,
  alertCircle,
  arrowBackOutline,
  arrowDownOutline,
  arrowForward,
  arrowForwardCircle,
  arrowForwardOutline,
  arrowUndoOutline,
  arrowUpOutline,
  bagHandle,
  brushOutline,
  buildOutline,
  carOutline,
  cartOutline,
  chatbubbleEllipses,
  checkmarkCircle,
  checkmarkCircleOutline,
  checkmarkDoneOutline,
  checkmarkOutline,
  chevronBackOutline,
  chevronDownOutline,
  chevronForwardOutline,
  chevronUpOutline,
  close,
  closeCircle,
  closeOutline,
  cloudDownload,
  cloudUploadOutline,
  contractOutline,
  create,
  createOutline,
  cubeOutline,
  document as documentIcon,
  documentOutline,
  documentTextOutline,
  download,
  downloadOutline,
  expandOutline,
  eyeOffOutline,
  flameOutline,
  flashSharp,
  gridOutline,
  hourglassOutline,
  imageOutline,
  imagesOutline,
  informationCircleOutline,
  layersOutline,
  locateOutline,
  logOutOutline,
  mailOutline,
  menuOutline,
  moveOutline,
  personOutline,
  qrCodeOutline,
  refreshOutline,
  removeCircle,
  reorderThreeOutline,
  repeat,
  saveOutline,
  search,
  searchOutline,
  settingsOutline,
  sunnyOutline,
  swapHorizontalOutline,
  swapVerticalOutline,
  textOutline,
  trash,
  trashBin,
  trashOutline,
  warning,
} from 'ionicons/icons';
// #endregion Imports

/**
 * Icone dell'app con la build standalone di Ionic.
 *
 * Con la build standalone `ion-icon` trova un'icona per nome solo in due modi: nella mappa registrata con
 * `addIcons()`, oppure scaricando `svg/<nome>.svg` dal percorso degli asset; se nessuno dei due è
 * impostato l'icona resta vuota, con un avviso in console
 * (`node_modules/ionicons/dist/collection/components/icon/utils.js:70-88`). Qui si usano entrambi:
 *
 * - `addIcons()` per tutti i nomi scritti nel codice (template e opzioni degli overlay, compreso il
 *   pulsante `close-outline` del toast dell'editor di immagini). Gli SVG entrano nel bundle e non
 *   servono richieste di rete. Un nome nuovo usato nel codice va aggiunto a `APP_ICONS`, altrimenti
 *   passa dal ripiego qui sotto e costa una richiesta.
 * - il percorso degli asset per ogni altro nome, che arriva dai dati e non si può elencare: le icone
 *   social del footer (content `icon_section_social_<n>`, es. `logo-facebook`) e gli `<ion-icon>`
 *   scritti nell'HTML dei content (es. `warning-outline` nel testo del modale dei prodotti da ordinare).
 *   Si scaricano da `svg/<nome>.svg` sotto la base dell'app, dove `angular.json` copia gli SVG di
 *   ionicons (`node_modules/ionicons/dist/ionicons/svg` → `./svg`), cioè lo stesso indirizzo della
 *   build lazy di prima.
 *
 * Le icone interne dei componenti Ionic (freccia del back-button, chevron di accordion e item, croce di
 * searchbar e input, …) non passano di qui: Ionic le importa già come dati SVG.
 */

// #region Constants
/**
 * Nomi delle icone usate dal codice (forma kebab-case, quella dei template), con l'SVG di ionicons.
 * Le chiavi sono scritte per esteso così la mappa registra esattamente i nomi dei template; `document`
 * è importato con un alias per non nascondere l'oggetto globale `document`.
 */
const APP_ICONS: Readonly<Record<string, string>> = {
    'add': add,
    'add-circle': addCircle,
    'add-circle-outline': addCircleOutline,
    'airplane': airplane,
    'airplane-outline': airplaneOutline,
    'alert-circle': alertCircle,
    'arrow-back-outline': arrowBackOutline,
    'arrow-down-outline': arrowDownOutline,
    'arrow-forward': arrowForward,
    'arrow-forward-circle': arrowForwardCircle,
    'arrow-forward-outline': arrowForwardOutline,
    'arrow-undo-outline': arrowUndoOutline,
    'arrow-up-outline': arrowUpOutline,
    'bag-handle': bagHandle,
    'brush-outline': brushOutline,
    'build-outline': buildOutline,
    'car-outline': carOutline,
    'cart-outline': cartOutline,
    'chatbubble-ellipses': chatbubbleEllipses,
    'checkmark-circle': checkmarkCircle,
    'checkmark-circle-outline': checkmarkCircleOutline,
    'checkmark-done-outline': checkmarkDoneOutline,
    'checkmark-outline': checkmarkOutline,
    'chevron-back-outline': chevronBackOutline,
    'chevron-down-outline': chevronDownOutline,
    'chevron-forward-outline': chevronForwardOutline,
    'chevron-up-outline': chevronUpOutline,
    'close': close,
    'close-circle': closeCircle,
    'close-outline': closeOutline,
    'cloud-download': cloudDownload,
    'cloud-upload-outline': cloudUploadOutline,
    'contract-outline': contractOutline,
    'create': create,
    'create-outline': createOutline,
    'cube-outline': cubeOutline,
    'document': documentIcon,
    'document-outline': documentOutline,
    'document-text-outline': documentTextOutline,
    'download': download,
    'download-outline': downloadOutline,
    'expand-outline': expandOutline,
    'eye-off-outline': eyeOffOutline,
    'flame-outline': flameOutline,
    'flash-sharp': flashSharp,
    'grid-outline': gridOutline,
    'hourglass-outline': hourglassOutline,
    'image-outline': imageOutline,
    'images-outline': imagesOutline,
    'information-circle-outline': informationCircleOutline,
    'layers-outline': layersOutline,
    'locate-outline': locateOutline,
    'log-out-outline': logOutOutline,
    'mail-outline': mailOutline,
    'menu-outline': menuOutline,
    'move-outline': moveOutline,
    'person-outline': personOutline,
    'qr-code-outline': qrCodeOutline,
    'refresh-outline': refreshOutline,
    'remove-circle': removeCircle,
    'reorder-three-outline': reorderThreeOutline,
    'repeat': repeat,
    'save-outline': saveOutline,
    'search': search,
    'search-outline': searchOutline,
    'settings-outline': settingsOutline,
    'sunny-outline': sunnyOutline,
    'swap-horizontal-outline': swapHorizontalOutline,
    'swap-vertical-outline': swapVerticalOutline,
    'text-outline': textOutline,
    'trash': trash,
    'trash-bin': trashBin,
    'trash-outline': trashOutline,
    'warning': warning,
};
// #endregion Constants

// #region PublicMethods
/**
 * Registra le icone del codice e il percorso degli SVG per tutte le altre. Va chiamata una sola volta
 * all'avvio, prima che si disegni la prima icona: non dipende da nessun servizio dell'app.
 */
export function registerAppIcons(): void {
  addIcons(APP_ICONS);

  // Stessa base della build lazy di prima: la cartella del documento risolta da <base href>
  // (`@ionic/core/dist/esm/index-BpRUsN_W.js:3532`, `new URL('./', document.baseURI)`). Con
  // `<base href="/">` gli SVG arrivano da `/svg/<nome>.svg`, e se l'app viene pubblicata sotto un
  // percorso diverso la base lo segue senza modifiche al codice.
  const assetBaseUrl: string = new URL('./', document.baseURI).href;

  // Il percorso va dato a entrambi i runtime: `ion-icon` è definito sia da ionicons (il componente
  // `IonIcon` di @ionic/angular importa `ionicons/components/ion-icon.js`) sia dalla copia che
  // @ionic/core porta con sé per le icone dei propri componenti (`@ionic/core/components`, che legge il
  // percorso dal proprio runtime). Vince la prima delle due che si registra come elemento, e l'ordine
  // dipende dal bundle: impostandolo su tutti e due, un nome non registrato si carica comunque da `svg/`.
  setIoniconsAssetPath(assetBaseUrl);
  setIonicCoreAssetPath(assetBaseUrl);
}
// #endregion PublicMethods
